<script>
  import { createEventDispatcher } from 'svelte';
  import { X, Package } from 'lucide-svelte';
  import { productsApi } from '../stores/api.js';
  import RichTextEditor from './RichTextEditor.svelte';

  export let show = false;
  export let product = null;

  let loading = false;
  let error = '';

  // Form fields
  let formData = {
    code: '',
    name: '',
    description: '',
    isActive: true
  };

  const dispatch = createEventDispatcher();

  // Reset form when modal opens
  $: if (show) {
    if (product) {
      // Edit mode
      formData = {
        code: product.code || '',
        name: product.name || '',
        description: product.description || '',
        isActive: product.isActive !== false
      };
    } else {
      // Create mode
      resetForm();
    }
    error = '';
  }

  function resetForm() {
    formData = {
      code: '',
      name: '',
      description: '',
      isActive: true
    };
  }

  function handleClose() {
    if (loading) return;
    show = false;
    resetForm();
    dispatch('close');
  }

  function handleKeydown(event) {
    if (event.key === 'Escape' && !loading) {
      handleClose();
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    try {
      loading = true;
      error = '';

      // Validate required fields
      if (!formData.code.trim()) {
        error = 'Code is required';
        return;
      }

      if (!formData.name.trim()) {
        error = 'Name is required';
        return;
      }

      // Prepare data
      const data = {
        code: formData.code.trim(),
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        isActive: formData.isActive
      };

      console.log('💾 Saving product:', data);

      let result;
      if (product) {
        // Update existing product
        result = await productsApi.update(product.id, data);
        console.log('✅ Product updated:', result);
      } else {
        // Create new product
        result = await productsApi.create(data);
        console.log('✅ Product created:', result);
      }

      // Success
      dispatch('success', result);
      handleClose();

    } catch (err) {
      error = err.message;
      console.error('❌ Save product error:', err);
    } finally {
      loading = false;
    }
  }
</script>

<svelte:window on:keydown={handleKeydown} />

{#if show}
  <!-- Modal backdrop -->
  <div class="modal-backdrop fade-in">
    <!-- Modal -->
    <div class="modal-content max-w-2xl w-full scale-in">
      <!-- Header -->
      <div class="px-6 py-5 border-b border-gray-200/60 dark:border-gray-700 flex items-center justify-between bg-linear-to-r from-primary-50/50 dark:from-primary-900/30 to-transparent">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 bg-linear-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
            <Package class="h-5 w-5 text-white" />
          </div>
          <div>
            <h2 class="text-lg font-bold text-gray-900 dark:text-white">
              {product ? 'Edit Product' : 'New Product'}
            </h2>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              {product ? 'Update product details' : 'Add a new product to the catalog'}
            </p>
          </div>
        </div>
        {#if !loading}
          <button
            onclick={handleClose}
            class="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200"
          >
            <X size={20} />
          </button>
        {/if}
      </div>

      <!-- Content -->
      <form onsubmit={handleSubmit}>
        <div class="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {#if error}
            <div class="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-3">
              <div class="text-sm text-red-800 dark:text-red-200">{error}</div>
            </div>
          {/if}

          <div class="space-y-4">
            <!-- Code -->
            <div>
              <label for="code" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Code *
              </label>
              <input
                id="code"
                type="text"
                bind:value={formData.code}
                placeholder="e.g. MAINT"
                class="input w-full"
                disabled={loading || (product !== null)}
                required
              />
              {#if product}
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Code cannot be modified</p>
              {/if}
            </div>

            <!-- Name -->
            <div>
              <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name *
              </label>
              <input
                id="name"
                type="text"
                bind:value={formData.name}
                placeholder="Product name"
                class="input w-full"
                disabled={loading}
                required
              />
            </div>

            <!-- Description -->
            <div>
              <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <RichTextEditor
                value={formData.description}
                placeholder="Detailed product description"
                disabled={loading}
                on:change={(e) => formData.description = e.detail}
              />
            </div>

            <!-- Active Status -->
            <div class="flex items-center space-x-3">
              <input
                id="isActive"
                type="checkbox"
                bind:checked={formData.isActive}
                class="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                disabled={loading}
              />
              <label for="isActive" class="text-sm font-medium text-gray-700 dark:text-gray-300">
                Active product
              </label>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
          <button
            type="button"
            onclick={handleClose}
            class="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            disabled={loading || !formData.code.trim() || !formData.name.trim()}
          >
            {#if loading}
              <svg class="animate-spin -ml-1 mr-3 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            {/if}
            {product ? 'Update' : 'Create Product'}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}
